import { useEffect, useRef, useState } from 'react';
import { Illustration } from '../../components/Illustration';
import { cn } from '../../lib/utils';
import { paidBackPercent } from '../../lib/equipmentInsights';
import { prefersReducedMotion, usePlayOnView } from './motion';
import { gearArt, shortName } from './gear';

// "Bought. Booked. Nearly paid for itself." as one picture. The kit's
// drawings glide along a smooth curve that runs the width of the section: in at the
// left, down under the heading, up to a peak at the top right and away off
// the right edge. The priciest piece, the camera, sits under the peak in a
// ring that fills as it pays itself back, and the kit passes behind it on
// the way over. A job counter ticks through the camera's jobs; each adds its
// days and its earnings and the ring fills a step. The totals it lands on are
// the template's own; the steps between share them out evenly, as a count-up
// would. Each piece shrinks as it nears the camera.

const RING = 172; // the ring's radius, in the 400-unit square it is drawn in
const CIRCUMFERENCE = 2 * Math.PI * RING;
const JOBS_AT = 1500; // when the first job lands
const JOB_MS = 480; // between jobs

const FLOW_MS = 32000; // how long a piece takes to cross
const PIECES = 14; // pieces on the curve at once (the kit, round again)

// The curve the kit follows on wide screens, as fractions of the block's
// width and height: in at the left, a dip under the heading, the peak at the
// top right just over the camera, then away down the right. The ring's place
// (its classes, below) matches the peak.
const CURVE_WIDE = [[-0.04, 0.47], [0.12, 0.57], [0.3, 0.71], [0.44, 0.65], [0.57, 0.48], [0.68, 0.25], [0.77, 0.17], [0.85, 0.21], [0.92, 0.37], [1.04, 0.54]];
const NARROW = 1024; // below this (Tailwind's lg), the phone layout

// Phones and tablets: the camera centred under the heading, and the kit in a
// straight line along the bottom. `bottom` is the line's distance from the
// block's bottom edge, in pixels: half a piece and a margin. The line starts
// two widths off the left edge, so the pieces, spread along all of it, arrive
// spaced apart rather than in a pile.
function narrowLine(width, height) {
  const bottom = width < 640 ? 52 : 68; // pieces are w-16, then sm:w-24
  const y = (height - bottom) / height;
  return [[-2, y], [1.1, y]];
}

// A smooth line through the points (Catmull-Rom), sampled finely, with each
// sample's distance along it, so pieces move at an even pace
function sampleCurve(points, width, height, count = 240) {
  const p = points.map(([x, y]) => [x * width, y * height]);
  const out = [];
  for (let i = 0; i < p.length - 1; i += 1) {
    const p0 = p[Math.max(0, i - 1)];
    const [p1, p2] = [p[i], p[i + 1]];
    const p3 = p[Math.min(p.length - 1, i + 2)];
    const steps = Math.ceil(count / (p.length - 1));
    for (let k = 0; k < steps; k += 1) {
      const t = k / steps;
      const t2 = t * t;
      const t3 = t2 * t;
      const at = (a, b, c, d) => 0.5 * (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
      out.push([at(p0[0], p1[0], p2[0], p3[0]), at(p0[1], p1[1], p2[1], p3[1])]);
    }
  }
  out.push(p[p.length - 1]);
  const lengths = [0];
  for (let i = 1; i < out.length; i += 1) lengths.push(lengths[i - 1] + Math.hypot(out[i][0] - out[i - 1][0], out[i][1] - out[i - 1][1]));
  return { points: out, lengths, total: lengths[lengths.length - 1] };
}

// Where a piece is, `s` of the way along the curve
function pointAt(s, curve) {
  const target = s * curve.total;
  let i = 1;
  while (i < curve.lengths.length - 1 && curve.lengths[i] < target) i += 1;
  const [ax, ay] = curve.points[i - 1];
  const [bx, by] = curve.points[i];
  const f = (target - curve.lengths[i - 1]) / (curve.lengths[i] - curve.lengths[i - 1] || 1);
  return { x: ax + (bx - ax) * f, y: ay + (by - ay) * f };
}

function GearCurve({ items, ringRef, running, instant }) {
  const stageRef = useRef(null);
  const pathRef = useRef(null);
  const pieceRefs = useRef([]);
  const pieces = Array.from({ length: PIECES }, (_, i) => items[i % items.length]);
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !ringRef.current) return undefined;
    let frame = 0;
    let curve;
    let ring;
    const measure = () => {
      const w = stage.offsetWidth;
      const h = stage.offsetHeight;
      curve = sampleCurve(w < NARROW ? narrowLine(w, h) : CURVE_WIDE, w, h);
      const box = stage.getBoundingClientRect();
      const r = ringRef.current.getBoundingClientRect();
      ring = { x: r.left - box.left + r.width / 2, y: r.top - box.top + r.height / 2, radius: r.width / 2 };
      const line = curve.points.map(([x, y]) => ({ x, y }));
      pathRef.current?.setAttribute('d', line.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' '));
    };
    const start = window.performance.now();
    const place = (now) => {
      const flow = instant ? 0.35 : (now - start) / FLOW_MS;
      pieceRefs.current.forEach((el, i) => {
        if (!el) return;
        const s = (flow + i / PIECES) % 1;
        const { x, y } = pointAt(s, curve);
        // Shrinking as it nears the camera, as if going round it into the
        // distance, and behind the ring over it, never across its figure
        const fromRing = Math.hypot(x - ring.x, y - ring.y);
        const near = Math.min(1, Math.max(0, (fromRing - ring.radius) / (ring.radius * 1.6)));
        el.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px) scale(${0.45 + 0.55 * near})`;
        el.style.opacity = String(0.55 + 0.45 * near);
        const inRing = fromRing < ring.radius * 1.1;
        el.style.zIndex = inRing ? '1' : '3';
      });
    };
    measure();
    const tick = (now) => {
      place(now);
      if (running && !instant) frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    const observer = window.ResizeObserver ? new window.ResizeObserver(() => {
      measure();
      place(window.performance.now());
    }) : null;
    observer?.observe(stage);
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [ringRef, running, instant]);

  return (
    <div ref={stageRef} className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <svg className="absolute inset-0 w-full h-full">
        <path ref={pathRef} fill="none" stroke="currentColor" strokeOpacity="0.22" strokeWidth="1.5" strokeDasharray="4 7" />
      </svg>
      {pieces.map((item, i) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          ref={(el) => { pieceRefs.current[i] = el; }}
          className="absolute left-0 top-0 w-16 sm:w-24 lg:w-28 will-change-transform"
          style={{ opacity: 0 }}
        >
          <Illustration name={gearArt(item.name)} inverse className="w-full aspect-square" />
        </div>
      ))}
    </div>
  );
}

// A number that eases from wherever it is to each new value, so a figure that
// climbs a step at a time never drops back to zero between steps
function useEased(value, ms, instant) {
  const [shown, setShown] = useState(value);
  const shownRef = useRef(value);
  useEffect(() => {
    if (instant) {
      shownRef.current = value;
      setShown(value);
      return undefined;
    }
    const from = shownRef.current;
    const start = window.performance.now();
    let frame;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / ms);
      const v = from + (value - from) * (1 - Math.pow(1 - t, 3));
      shownRef.current = v;
      setShown(v);
      if (t < 1) frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [value, ms, instant]);
  return shown;
}


// `wrap` is the page's content width, for the heading and captions; the curve
// itself runs the full width. `children` is the section's heading.
export function KitOrbit({ story, equipment, money, wrap, children }) {
  const ref = useRef(null);
  const inView = usePlayOnView(ref, 0.3);
  const ringRef = useRef(null);
  const [instant] = useState(prefersReducedMotion);
  const hero = story.priciest;
  const jobs = Math.max(1, hero.jobs);
  const [counting, setCounting] = useState(false); // the job counter showing
  const [done, setDone] = useState(0); // jobs counted so far

  // The rest of the kit with a drawing of its own, to ride the curve
  const riding = equipment.filter((e) => e.name !== hero.name && gearArt(e.name) !== 'gearCamera');

  useEffect(() => {
    if (instant) {
      setCounting(true);
      setDone(jobs);
      return undefined;
    }
    if (!inView) {
      setCounting(false);
      setDone(0);
      return undefined;
    }
    const timers = [setTimeout(() => setCounting(true), JOBS_AT - 200)];
    for (let n = 1; n <= jobs; n += 1) timers.push(setTimeout(() => setDone(n), JOBS_AT + (n - 1) * JOB_MS));
    return () => timers.forEach(clearTimeout);
  }, [inView, instant, jobs]);

  const earned = (hero.total_income * done) / jobs;
  const percent = paidBackPercent(earned, hero.purchase_price);
  const shownPercent = useEased(percent, JOB_MS, instant);

  return (
    <div ref={ref} className="flex flex-col">
      {/* Below lg the block grows to fit: heading, ring, then room for the
          counter and the line of kit (the bottom padding) */}
      <div className="relative pb-[150px] sm:pb-[180px] lg:pb-0 lg:h-[640px]">
        <GearCurve items={riding} ringRef={ringRef} running={inView} instant={instant} />

        {/* The heading, over the curve */}
        <div className={`${wrap} relative z-[5] pt-14 sm:pt-20 lg:pt-32`}>{children}</div>

        {/* The camera, in its ring: centred under the heading below lg; on wide
            screens well under the curve's peak, so the kit passes over it */}
        <div
          ref={ringRef}
          className="relative mx-auto mt-10 z-[2] w-[200px] sm:w-[240px] aspect-square lg:absolute lg:m-0 lg:left-[77%] lg:top-[53%] lg:-translate-x-1/2 lg:-translate-y-1/2 lg:w-[300px]"
        >
          <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full -rotate-90" aria-hidden="true">
            <circle cx="200" cy="200" r={RING} fill="hsl(var(--inverse))" stroke="currentColor" strokeOpacity="0.15" strokeWidth="10" />
            <circle
              cx="200"
              cy="200"
              r={RING}
              fill="none"
              stroke="hsl(var(--moss-soft))"
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={CIRCUMFERENCE * (1 - Math.min(percent, 100) / 100)}
              className={cn(!instant && 'transition-[stroke-dashoffset] duration-500 ease-out')}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-center">
            <Illustration name="gearCamera" inverse className="w-[54%] aspect-square -mb-5" />
            <p className="text-4xl sm:text-5xl font-light tracking-tight leading-none tabular-nums">{Math.round(shownPercent)}%</p>
            <p className="text-sm text-inverse-muted">paid back</p>
          </div>
          {/* The job counter */}
          <p
            className={cn(
              'absolute left-1/2 top-full mt-5 -translate-x-1/2 z-[4] rounded-pill bg-inverse-foreground/10 px-4 py-1.5 text-sm whitespace-nowrap tabular-nums transition-opacity duration-300',
              counting ? 'opacity-100' : 'opacity-0',
            )}
          >
            <span className="hidden sm:inline">{shortName(hero.name)} · </span>job {Math.max(done, 1)} of {jobs} · {money(earned)} earned
          </p>
        </div>
      </div>

    </div>
  );
}
